import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  getSolicitaDebMunicipioContas,
  listarAtivosSolicitaDebMunicipio,
  uploadSolicitaDebMunicipioAnexo,
  inserirSolicitaDebMunicipio,
} from '~/Services/Service';

const PREFIXO_DEBITOS = 'SOLICITAÇÃO DE DÉBITOS';
const TIPO_REPASSE_CR = 'REPASSE DE CRÉDITO';
const WHITELIST_EXTENSOES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.zip', '.rar'];

const buildSchema = (tiposSolicDebito) =>
  Yup.object().shape({
    codigo_familia: Yup.number()
      .nullable()
      .required('Empreendimento obrigatório'), // V-01
    codigo_grupo_empresa: Yup.number()
      .nullable()
      .required('Grupo Empresa obrigatório'), // V-01
    codigo_conta: Yup.number()
      .nullable()
      .required('Conta obrigatória'), // V-01
    codigo_ativo: Yup.number()
      .nullable()
      .required('Ativo obrigatório'), // V-01 — boleto sai do ativo selecionado
    codigo_tipo_solicitacao: Yup.number()
      .nullable()
      .required('Tipo de solicitação obrigatório'), // V-02
    validade: Yup.string()
      .nullable()
      .when('codigo_tipo_solicitacao', (tipo, s) => {
        // V-03: Validade obrigatória se tipo começa com PREFIXO_DEBITOS
        const ehDebito = tiposSolicDebito.includes(Number(tipo));
        return ehDebito
          ? s.required('Validade obrigatória para este tipo')
          : s;
      }),
    memo: Yup.string().max(500, 'Máximo 500 caracteres'),
  });

const NovaSolicitacaoModal = ({ show, onHide, onSuccess, gruposEmpresa, tiposSolicitacao }) => {
  const [contas, setContas] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [carregandoAtivos, setCarregandoAtivos] = useState(false);
  const [incluirRescindidos, setIncluirRescindidos] = useState(false);
  const [anexosPendentes, setAnexosPendentes] = useState([]);
  const [uploadando, setUploadando] = useState(false);

  const empreendimentosRedux = useSelector((state) => state.empreendimentos);
  const optionsEmpreendimentos = (empreendimentosRedux && empreendimentosRedux.sort
    ? empreendimentosRedux.sort
    : []).map((item) => ({
    value: item.codigo,
    label: `${item.codigo} - ${item.descricao}`,
  }));

  const tiposSolicDebito = tiposSolicitacao
    .filter((t) => t.assunto && t.assunto.startsWith(PREFIXO_DEBITOS))
    .map((t) => t.value);

  const tiposRepasse = tiposSolicitacao
    .filter((t) => t.assunto && t.assunto.toUpperCase().indexOf(TIPO_REPASSE_CR) >= 0)
    .map((t) => t.value);

  const schema = buildSchema(tiposSolicDebito);

  useEffect(() => {
    if (!show) {
      setAnexosPendentes([]);
      setContas([]);
      setAtivos([]);
      setIncluirRescindidos(false);
    }
  }, [show]);

  const carregarContas = async (codigoGE) => {
    if (!codigoGE) { setContas([]); return; }
    try {
      const resp = await getSolicitaDebMunicipioContas(codigoGE);
      setContas((resp || []).map((c) => ({ value: c.codigo, label: `${c.codigo} - ${c.apelido}` })));
    } catch (err) {
      setContas([]);
    }
  };

  const carregarAtivos = async (codigoFamilia, codigoGrupoEmpresa, codigoConta, incluirRes = incluirRescindidos) => {
    if (!codigoFamilia || !codigoGrupoEmpresa || !codigoConta) {
      setAtivos([]);
      return;
    }
    setCarregandoAtivos(true);
    try {
      const resp = await listarAtivosSolicitaDebMunicipio(
        codigoFamilia, codigoGrupoEmpresa, codigoConta, incluirRes
      );
      setAtivos(resp || []);
    } catch (err) {
      setAtivos([]);
      toast.error(err.message);
    } finally {
      setCarregandoAtivos(false);
    }
  };

  const handleArquivoChange = async (e) => {
    const inputEl = e.target;
    const arquivos = Array.from(inputEl.files || []);
    const invalidos = arquivos.filter((f) => {
      const ext = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
      return !WHITELIST_EXTENSOES.includes(ext);
    });
    if (invalidos.length > 0) {
      toast.error(`Extensão não permitida: ${invalidos.map((f) => f.name).join(', ')}`);
      if (inputEl) inputEl.value = '';
      return;
    }
    if (anexosPendentes.length + arquivos.length > 10) {
      toast.error('Máximo 10 anexos por solicitação.');
      if (inputEl) inputEl.value = '';
      return;
    }
    setUploadando(true);
    const novos = [];
    for (const arquivo of arquivos) {
      const fd = new FormData();
      fd.append('file', arquivo);
      try {
        const resp = await uploadSolicitaDebMunicipioAnexo(fd);
        novos.push({ token: resp && resp.token, nome: arquivo.name });
      } catch (err) {
        toast.error(`Falha ao enviar ${arquivo.name}: ${err.message}`);
      }
    }
    setAnexosPendentes((prev) => [...prev, ...novos]);
    setUploadando(false);
    if (inputEl) inputEl.value = '';
  };

  const removerAnexo = (token) => {
    setAnexosPendentes((prev) => prev.filter((a) => a.token !== token));
  };

  const handleSubmit = async (values, formikBag) => {
    try {
      const ativoSelecionado = ativos.find((a) => a.codigo_ativo === values.codigo_ativo);
      if (!ativoSelecionado) {
        toast.error('Selecione um ativo.');
        formikBag.setSubmitting(false);
        return;
      }
      const payload = {
        codigo_boleto: ativoSelecionado.codigo_boleto,
        codigo_tipo_solicitacao: values.codigo_tipo_solicitacao,
        validade: values.validade || null,
        observacao: values.memo || '',
        pagar_iptu_mes_atual: !!values.pagar_iptu_mes_atual,
        pagar_iptu_mes_seguinte: !!values.pagar_iptu_mes_seguinte,
        anexos_tokens: anexosPendentes.map((a) => a.token),
      };
      await inserirSolicitaDebMunicipio(payload);
      toast.success('Solicitação criada com sucesso.');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  const initialValues = {
    codigo_familia: null,
    codigo_grupo_empresa: null,
    codigo_conta: null,
    codigo_ativo: null,
    codigo_tipo_solicitacao: null,
    validade: '',
    memo: '',
    pagar_iptu_mes_atual: false,
    pagar_iptu_mes_seguinte: false,
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          Nova Solicitação de Débito Municipal
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const tipoSelecionado = tiposSolicitacao.find(
            (t) => t.value === formik.values.codigo_tipo_solicitacao
          );
          const assuntoTipo = tipoSelecionado ? (tipoSelecionado.assunto || '') : '';
          const ehTipoDebito = assuntoTipo.startsWith(PREFIXO_DEBITOS);
          const ehTipoRepasse = tiposRepasse.includes(formik.values.codigo_tipo_solicitacao);

          return (
            <Form onSubmit={formik.handleSubmit}>
              <Modal.Body>
                {/* Seção 1: Empreendimento / Grupo Empresa / Conta / Ativo */}
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Empreendimento *</Form.Label>
                      <Select
                        placeholder="Selecione..."
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        options={optionsEmpreendimentos}
                        value={
                          optionsEmpreendimentos.find((e) => e.value === formik.values.codigo_familia) || null
                        }
                        onChange={(opt) => {
                          const novoFamilia = opt ? opt.value : null;
                          formik.setFieldValue('codigo_familia', novoFamilia);
                          formik.setFieldValue('codigo_ativo', null);
                          carregarAtivos(
                            novoFamilia,
                            formik.values.codigo_grupo_empresa,
                            formik.values.codigo_conta
                          );
                        }}
                      />
                      {formik.touched.codigo_familia && formik.errors.codigo_familia && (
                        <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                          {formik.errors.codigo_familia}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Grupo Empresa *</Form.Label>
                      <Select
                        placeholder="Selecione..."
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        options={gruposEmpresa}
                        value={
                          gruposEmpresa.find((g) => g.value === formik.values.codigo_grupo_empresa) || null
                        }
                        onChange={(opt) => {
                          const novoGE = opt ? opt.value : null;
                          formik.setFieldValue('codigo_grupo_empresa', novoGE);
                          formik.setFieldValue('codigo_conta', null);
                          formik.setFieldValue('codigo_ativo', null);
                          setAtivos([]);
                          carregarContas(novoGE);
                        }}
                      />
                      {formik.touched.codigo_grupo_empresa && formik.errors.codigo_grupo_empresa && (
                        <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                          {formik.errors.codigo_grupo_empresa}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Conta *</Form.Label>
                      <Select
                        placeholder="Selecione..."
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        options={contas}
                        value={contas.find((c) => c.value === formik.values.codigo_conta) || null}
                        onChange={(opt) => {
                          const novaConta = opt ? opt.value : null;
                          formik.setFieldValue('codigo_conta', novaConta);
                          formik.setFieldValue('codigo_ativo', null);
                          carregarAtivos(
                            formik.values.codigo_familia,
                            formik.values.codigo_grupo_empresa,
                            novaConta
                          );
                        }}
                        isDisabled={!formik.values.codigo_grupo_empresa}
                      />
                      {formik.touched.codigo_conta && formik.errors.codigo_conta && (
                        <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                          {formik.errors.codigo_conta}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label className="font-weight-bold mb-0">Ativo (Quadra/Lote) *</Form.Label>
                        <Form.Check
                          type="checkbox"
                          id="incluir-rescindidos"
                          label="Incluir contratos rescindidos"
                          checked={incluirRescindidos}
                          onChange={(e) => {
                            const novoValor = e.target.checked;
                            setIncluirRescindidos(novoValor);
                            formik.setFieldValue('codigo_ativo', null);
                            carregarAtivos(
                              formik.values.codigo_familia,
                              formik.values.codigo_grupo_empresa,
                              formik.values.codigo_conta,
                              novoValor
                            );
                          }}
                          style={{ fontSize: '0.85rem' }}
                        />
                      </div>
                      <Select
                        placeholder={
                          carregandoAtivos ? 'Carregando...' :
                          !formik.values.codigo_familia || !formik.values.codigo_grupo_empresa || !formik.values.codigo_conta
                            ? 'Selecione Empreendimento, GE e Conta'
                            : ativos.length === 0 ? 'Nenhum ativo encontrado' : 'Selecione...'
                        }
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        isLoading={carregandoAtivos}
                        options={ativos.map((a) => ({
                          value: a.codigo_ativo,
                          label: `Q ${a.quadra || '-'} L ${a.lote || '-'} — ${a.nome || ''} (Boleto ${a.codigo_boleto})`,
                        }))}
                        value={(() => {
                          const sel = ativos.find((a) => a.codigo_ativo === formik.values.codigo_ativo);
                          return sel
                            ? {
                                value: sel.codigo_ativo,
                                label: `Q ${sel.quadra || '-'} L ${sel.lote || '-'} — ${sel.nome || ''} (Boleto ${sel.codigo_boleto})`,
                              }
                            : null;
                        })()}
                        onChange={(opt) =>
                          formik.setFieldValue('codigo_ativo', opt ? opt.value : null)
                        }
                        isDisabled={ativos.length === 0}
                      />
                      {formik.touched.codigo_ativo && formik.errors.codigo_ativo && (
                        <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                          {formik.errors.codigo_ativo}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Seção 2: Tipo */}
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Tipo de Solicitação *</Form.Label>
                      <Select
                        placeholder="Selecione..."
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        options={tiposSolicitacao}
                        value={
                          tiposSolicitacao.find(
                            (t) => t.value === formik.values.codigo_tipo_solicitacao
                          ) || null
                        }
                        onChange={(opt) =>
                          formik.setFieldValue('codigo_tipo_solicitacao', opt ? opt.value : null)
                        }
                      />
                      {formik.touched.codigo_tipo_solicitacao && formik.errors.codigo_tipo_solicitacao && (
                        <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                          {formik.errors.codigo_tipo_solicitacao}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  {/* V-03: Validade só aparece para SOLICITAÇÃO DE DÉBITOS */}
                  {ehTipoDebito && (
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="font-weight-bold">
                          Validade da Cotação *
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="validade"
                          value={formik.values.validade}
                          onChange={formik.handleChange}
                          min={new Date().toISOString().split('T')[0]}
                          isInvalid={formik.touched.validade && !!formik.errors.validade}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.validade}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                {/* V-06: Checkboxes só para REPASSE DE CRÉDITO */}
                {ehTipoRepasse && (
                  <Row className="mb-2">
                    <Col>
                      <Form.Label className="font-weight-bold d-block">
                        Parcelas (pelo menos uma) *
                      </Form.Label>
                      <Form.Check
                        inline
                        type="checkbox"
                        label="Parcela mês atual"
                        name="pagar_iptu_mes_atual"
                        checked={formik.values.pagar_iptu_mes_atual}
                        onChange={formik.handleChange}
                        id="chk-mes-atual"
                      />
                      <Form.Check
                        inline
                        type="checkbox"
                        label="Parcela mês seguinte"
                        name="pagar_iptu_mes_seguinte"
                        checked={formik.values.pagar_iptu_mes_seguinte}
                        onChange={formik.handleChange}
                        id="chk-mes-seguinte"
                      />
                      {formik.submitCount > 0 && ehTipoRepasse &&
                        !formik.values.pagar_iptu_mes_atual && !formik.values.pagar_iptu_mes_seguinte && (
                          <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                            Selecione pelo menos uma parcela.
                          </div>
                        )}
                    </Col>
                  </Row>
                )}

                {/* Observação */}
                <Form.Group>
                  <Form.Label className="font-weight-bold">Observação</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="memo"
                    value={formik.values.memo}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.memo && !!formik.errors.memo}
                    placeholder="Observações adicionais..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.memo}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Anexos */}
                <Form.Group>
                  <Form.Label className="font-weight-bold">Anexos</Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    accept={WHITELIST_EXTENSOES.join(',')}
                    onChange={handleArquivoChange}
                    disabled={uploadando}
                  />
                  <Form.Text className="text-muted">
                    Extensões permitidas: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG, GIF, ZIP, RAR. Máximo 10 arquivos.
                  </Form.Text>
                  {uploadando && (
                    <div className="text-info mt-1" style={{ fontSize: '0.85rem' }}>
                      Enviando arquivo...
                    </div>
                  )}
                  {anexosPendentes.length > 0 && (
                    <ul className="mt-2 mb-0 pl-3">
                      {anexosPendentes.map((a) => (
                        <li key={a.token} style={{ fontSize: '0.85rem' }}>
                          {a.nome}{' '}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-danger"
                            onClick={() => removerAnexo(a.token)}
                          >
                            remover
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Form.Group>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={formik.isSubmitting || uploadando}
                >
                  {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default NovaSolicitacaoModal;
